import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { Bar, Line, Pie } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { Heading } from 'src/components/Heading';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { SelectBox } from 'src/components/SelectBox';
import { useNotification } from 'src/contexts/NotificationContext';
import { useCallActions } from 'src/hooks/getCallActions';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { useCallsGroupedByHourPST } from 'src/hooks/useCallsGroupedByHourPST';
import { useCallsGroupedByStage } from 'src/hooks/useCallsGroupedByStage';
import { useCallsGroupedByStageOutcome } from 'src/hooks/useCallsGroupedByStageOutcome';
import { useCallsGroupedByStatus } from 'src/hooks/useCallsGroupedByStatus';
import useOrganization from 'src/hooks/useOrganization';
import { Agent } from 'src/interfaces/agent.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { PerDayUsage, Usage } from 'src/interfaces/usage.interface';
import {
  prepareCallActionsGraphData,
  prepareCallsGroupedByHourPSTGraphData,
  prepareCallsGroupedByStageGraphData,
  prepareCallsGroupedByStageOutcomeGraphData,
  prepareCallsGroupedByStatusGraphData,
  preparePerDayUsageGraphData,
} from 'src/utils/dashboard';
import { getFirstAndLastFourUUID } from 'src/utils/number';
import { useDebounce } from 'ahooks';
import { Can, Subject } from 'src/hooks/usePermissions';
import { Loading } from 'src/components/Loading';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
);

const callDirectionOptions = [
  { value: 'both', label: 'Both' },
  { value: 'incoming', label: 'Incoming' },
  { value: 'outgoing', label: 'Outgoing' },
];

export const Dashboard = () => {
  const { get } = useAuthedRequest();
  const notification = useNotification();

  const { organization } = useOrganization();

  const navigate = useNavigate();

  const [usage, setUsage] = useState<Usage | undefined>(undefined);
  const [perDayUsage, setPerDayUsage] = useState<PerDayUsage[]>([]);
  const [planLimit, setPlanLimit] = useState('unlimited');
  const [planType, setPlanType] = useState('unlimited');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [haveCalls, setHaveCalls] = useState(false);

  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [isLoadingUsage, setIsLoadingUsage] = useState(true);
  const [isLoadingPlanData, setIsLoadingPlanData] = useState(true);
  const [isLoadingOnboarding, setIsLoadingOnboarding] = useState(true);
  const [isLoadingCalls, setIsLoadingCalls] = useState(true);

  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    end.setDate(end.getDate() - 1);
    const start = new Date();
    start.setDate(start.getDate() - 7);

    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    };
  });
  const debouncedDateRange = useDebounce(dateRange, { wait: 500 });
  const [minDuration, setMinDuration] = useState('0');
  const [maxDuration, setMaxDuration] = useState('7200');
  const [callDirection, setCallDirection] = useState('both');

  const [agents, setAgents] = useState([]);

  const agentsOptions = [
    { value: 'All Agents' },
    ...agents.map((agent: { id: string }) => ({
      value: agent.id,
      label: getFirstAndLastFourUUID(agent.id),
    })),
  ];

  const searchAgents = useCallback(async (name: string) => {
    const queryObject: QueryObject = {
      ...(name.length !== 0 && { filters: JSON.stringify({ name }) }),
    };
    const query = new URLSearchParams(queryObject as Record<string, string>);

    const data = await get(`/agents?${query.toString()}`);

    setAgents(data.items);

    return data.items.map((agent: Agent) => ({
      value: agent.id,
      label: agent.name,
    }));
  }, []);

  const fetchPlanData = async () => {
    if (!organization.id) {
      setIsLoadingPlanData(false);
      return;
    }

    try {
      const org = await get(`/organizations/${organization.id}`);
      if (!org.planId) return;

      const plan = await get(`/plans/${org.planId}`);
      setPlanType(plan.name);

      if (!plan.featureGates.usage) return;
      setPlanLimit(plan.featureGates.usage);
    } catch (error) {
      console.error('Error fetching plan data:', error);
    } finally {
      setIsLoadingPlanData(false);
    }
  };

  const fetchUsage = async () => {
    try {
      const usageData = await get('/usage');
      setUsage(usageData);
    } catch (error) {
      console.error('Error fetching usage data:', error);
    } finally {
      setIsLoadingUsage(false);
    }
  };

  const fetchPerDayUsage = async () => {
    try {
      const query = new URLSearchParams({
        startDate: dateRange.start,
        endDate: dateRange.end,
      });
      const usageData = await get(`/stripe/usage?${query.toString()}`);
      setPerDayUsage(usageData);
    } catch (error) {
      console.error('Error fetching per minute usage data:', error);
    } finally {
      setIsLoadingUsage(false);
    }
  };

  const fetchOnboarding = async () => {
    if (!organization.id) {
      setIsLoadingOnboarding(false);
      return;
    }

    try {
      const response = await get(
        `/organizations/${organization.id}/onboarding`,
      );

      if (
        response &&
        Object.keys(response).length > 0 &&
        !response?.isCompleted
      ) {
        navigate(`/onboarding`);
      }
    } catch {
      notification.error('Error fetching onboarding data');
    } finally {
      setIsLoadingOnboarding(false);
    }
  };

  const fetchCalls = async () => {
    try {
      const response = await get(`/calls?size=1`);
      setHaveCalls(response?.items?.length > 0);
    } catch (error) {
      console.error('Error fetching calls data:', error);
    } finally {
      setIsLoadingCalls(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      setIsLoadingInitialData(true);

      if (!organization.id) {
        setIsLoadingInitialData(false);
        return;
      }

      await Promise.all([
        fetchPlanData(),
        fetchUsage(),
        fetchPerDayUsage(),
        fetchOnboarding(),
        fetchCalls(),
      ]);

      setIsLoadingInitialData(false);
    };

    initializeData();
  }, [organization.id]);

  useEffect(() => {
    fetchPerDayUsage();
  }, [debouncedDateRange]);

  const planUpperBound = isNaN(parseInt(planLimit))
    ? Number.MAX_VALUE
    : parseInt(planLimit);

  const groupedFilterObject = {
    startDate: new Date(
      new Date(`${debouncedDateRange.start}T00:00:00`).getTime() -
        new Date().getTimezoneOffset() * 60000,
    ),
    endDate: new Date(
      new Date(`${debouncedDateRange.end}T23:59:59`).getTime() -
        new Date().getTimezoneOffset() * 60000,
    ),
    minDurationSecs: parseInt(minDuration),
    maxDurationSecs: parseInt(maxDuration),
    ...(callDirection !== 'both' && {
      callDirection: callDirection as 'incoming' | 'outgoing',
    }),
    ...(selectedAgent !== 'All Agents' && {
      agentId: selectedAgent,
    }),
  };

  const {
    loading: callsGroupedByStatusLoading,
    data: callsGroupedByStatusData,
  } = useCallsGroupedByStatus(groupedFilterObject);

  const {
    loading: callsGroupedByStageOutcomeLoading,
    data: callsGroupedByStageOutcomeData,
  } = useCallsGroupedByStageOutcome(groupedFilterObject);

  const { loading: callsGroupedByStageLoading, data: callsGroupedByStageData } =
    useCallsGroupedByStage(groupedFilterObject);

  const {
    loading: callsGroupedByHourPSTLoading,
    data: callsGroupedByHourPSTData,
  } = useCallsGroupedByHourPST(groupedFilterObject);

  const { loading: incomingCallActionsLoading, data: incomingCallActionsData } =
    useCallActions({ ...groupedFilterObject, callDirection: 'incoming' });

  const { loading: outgoingCallActionsLoading, data: outgoingCallActionsData } =
    useCallActions({ ...groupedFilterObject, callDirection: 'outgoing' });

  const isLoadingGraphs =
    callsGroupedByStatusLoading ||
    callsGroupedByStageOutcomeLoading ||
    callsGroupedByStageLoading ||
    callsGroupedByHourPSTLoading ||
    incomingCallActionsLoading ||
    outgoingCallActionsLoading;

  const isLoading =
    isLoadingInitialData ||
    isLoadingUsage ||
    isLoadingPlanData ||
    isLoadingOnboarding ||
    isLoadingCalls;

  if (isLoading) {
    return (
      <div className="flex-1">
        <Heading title="Dashboard" />
        <div className="flex flex-col items-center justify-center py-12">
          <Loading size="8" />
          <p className="text-neutral-400 mt-4">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1">
      <Heading title="Dashboard" />

      <Can operation="read" subject={Subject.DASHBOARD}>
        {usage && (
          <>
            <div className="mt-4 mx-6 flex flex-wrap gap-6">
              <div className="p-4 border border-neutral-100 shadow-md transition w-full max-w-[300px] min-h-[132px] rounded-lg flex flex-col">
                <div className="flex items-center justify-between">
                  <div className="font-regular text-neutral-400">Plan Type</div>
                </div>
                <div className="text-2xl font-medium">{planType}</div>
              </div>

              <div className="p-4 border border-neutral-100 shadow-md transition w-full max-w-[300px] min-h-[132px] rounded-lg flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between">
                    <div className="font-regular text-neutral-400">
                      Monthly Usage
                    </div>
                  </div>
                  <div className="text-2xl font-medium">
                    {usage.monthly_usage_minutes} / {planLimit} minutes
                  </div>
                </div>
                {planLimit !== 'unlimited' && (
                  <div className="h-2 bg-neutral-100 rounded">
                    <div
                      className={clsx('rounded h-full', {
                        'bg-primary':
                          usage.monthly_usage_minutes < planUpperBound * 0.8,
                        'bg-warning':
                          usage.monthly_usage_minutes >= planUpperBound * 0.8 &&
                          usage.monthly_usage_minutes < planUpperBound,
                        'bg-error':
                          usage.monthly_usage_minutes >= planUpperBound,
                      })}
                      style={{
                        width: `${Math.min(100, (usage.monthly_usage_minutes / planUpperBound) * 100)}%`,
                      }}
                    />
                  </div>
                )}
              </div>

              <div
                className={clsx(
                  'p-4 border border-neutral-100 shadow-md transition w-full max-w-[300px] min-h-[132px] rounded-lg flex flex-col',
                  isNaN(usage.max_concurrent) && 'hidden',
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="font-regular text-neutral-400">
                    Max Concurrent Calls
                  </div>
                </div>
                <div className="text-2xl font-medium">
                  {usage.max_concurrent}
                </div>
              </div>
            </div>

            <div className="space-y-4 border-b-2 mt-4"></div>
          </>
        )}

        {!haveCalls && (
          <div>
            <ContentWrapper>
              <div className="flex flex-col gap-4 items-center">
                <div className="text-lg font-medium">
                  Make some calls to see your dashboard analytics
                </div>
              </div>
            </ContentWrapper>
          </div>
        )}

        {haveCalls && (
          <ContentWrapper>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
              <div className="flex flex-col">
                <Label>Agent</Label>

                <AutoCompleteSelectBox
                  defaultValue={agentsOptions.find(
                    (agent) => agent.value === selectedAgent,
                  )}
                  fetchOptions={searchAgents}
                  onChange={(value) => setSelectedAgent(value)}
                  variant="outlined"
                  color="primary"
                  size="medium"
                  disabled={isLoadingGraphs}
                  defaultOptions={agentsOptions}
                />
              </div>

              <div className="flex flex-col">
                <Label>Dates</Label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) =>
                      setDateRange({ ...dateRange, start: e.target.value })
                    }
                    variant="outlined"
                    color="primary"
                    size="medium"
                    className="flex-1"
                    disabled={isLoadingGraphs}
                  />
                  <Input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) =>
                      setDateRange({ ...dateRange, end: e.target.value })
                    }
                    variant="outlined"
                    color="primary"
                    size="medium"
                    className="flex-1"
                    disabled={isLoadingGraphs}
                  />
                </div>
              </div>

              <div className="flex flex-col">
                <Label>Incoming / Outgoing</Label>
                <SelectBox
                  options={callDirectionOptions}
                  value={callDirectionOptions.find(
                    (option) => option.value === callDirection,
                  )}
                  variant="outlined"
                  color="primary"
                  size="medium"
                  onChange={(value) => setCallDirection(value)}
                  className="h-[45px]"
                  disabled={isLoadingGraphs}
                />
              </div>

              <div className="flex flex-col">
                <Label>Min Duration Secs</Label>
                <Input
                  type="number"
                  value={minDuration}
                  onChange={(e) => setMinDuration(e.target.value)}
                  variant="outlined"
                  color="primary"
                  size="medium"
                  disabled={isLoadingGraphs}
                />
              </div>

              <div className="flex flex-col">
                <Input
                  label="Max Duration Secs"
                  type="number"
                  value={maxDuration}
                  onChange={(e) => setMaxDuration(e.target.value)}
                  variant="outlined"
                  color="primary"
                  size="medium"
                  disabled={isLoadingGraphs}
                />
              </div>
            </div>

            <div className="w-full mx-auto">
              {isLoadingGraphs ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loading size="8" />
                  <p className="text-neutral-400 mt-4">
                    Loading analytics data...
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
                  <div className="h-[300px] flex justify-center items-center bg-text-neutral-0 rounded shadow p-2">
                    <Bar
                      options={{
                        responsive: true,
                        plugins: {
                          title: {
                            display: true,
                            text: 'Usage by Day',
                          },
                          legend: {
                            position: 'right' as const,
                          },
                        },
                        scales: {
                          x: {
                            stacked: true,
                          },
                          y: {
                            stacked: true,
                          },
                        },
                      }}
                      data={preparePerDayUsageGraphData(
                        perDayUsage,
                        dateRange.start,
                        dateRange.end,
                      )}
                    />
                  </div>
                  <div className="h-[300px] flex justify-center items-center bg-text-neutral-0 rounded shadow p-2">
                    <Bar
                      options={{
                        responsive: true,
                        plugins: {
                          title: {
                            display: true,
                            text: 'Calls by Status',
                          },
                          legend: {
                            position: 'right' as const,
                          },
                        },
                        scales: {
                          x: {
                            stacked: true,
                          },
                          y: {
                            stacked: true,
                          },
                        },
                      }}
                      data={prepareCallsGroupedByStatusGraphData(
                        callsGroupedByStatusData || [],
                      )}
                    />
                  </div>
                  <div className="h-[300px] flex justify-center items-center bg-text-neutral-0 rounded shadow p-2">
                    <Bar
                      options={{
                        responsive: true,
                        plugins: {
                          title: {
                            display: true,
                            text: 'Disconnects by Human vs. Agent',
                          },
                          legend: {
                            position: 'right' as const,
                          },
                        },
                        scales: {
                          x: {
                            stacked: true,
                          },
                          y: {
                            stacked: true,
                          },
                        },
                      }}
                      data={prepareCallsGroupedByStageOutcomeGraphData(
                        callsGroupedByStageOutcomeData || [],
                      )}
                    />
                  </div>
                  <div className="h-[300px] flex justify-center items-center bg-text-neutral-0 rounded shadow p-2">
                    <Bar
                      options={{
                        responsive: true,
                        plugins: {
                          title: {
                            display: true,
                            text: 'Calls by Stage',
                          },
                          legend: {
                            position: 'right' as const,
                          },
                        },
                        scales: {
                          x: {
                            stacked: true,
                          },
                          y: {
                            stacked: true,
                          },
                        },
                      }}
                      data={prepareCallsGroupedByStageGraphData(
                        callsGroupedByStageData || [],
                      )}
                    />
                  </div>
                  <div className="h-[300px] flex justify-center items-center bg-text-neutral-0 rounded shadow p-2">
                    <Line
                      options={{
                        responsive: true,
                        plugins: {
                          title: {
                            display: true,
                            text: 'Calls by Hour (PST)',
                          },
                          legend: {
                            display: false,
                          },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                          },
                        },
                      }}
                      data={prepareCallsGroupedByHourPSTGraphData(
                        callsGroupedByHourPSTData || [],
                      )}
                    />
                  </div>
                </div>
              )}
            </div>
          </ContentWrapper>
        )}

        {haveCalls &&
          !isLoadingGraphs &&
          ((outgoingCallActionsData && outgoingCallActionsData.length > 0) ||
            (incomingCallActionsData &&
              incomingCallActionsData.length > 0)) && (
            <div className="border-t-2">
              <ContentWrapper>
                <div className="flex flex-col md:grid md:grid-cols-2 gap-4">
                  {(!outgoingCallActionsData ||
                    outgoingCallActionsData?.length > 0) && (
                    <div className="flex flex-col gap-4 items-center">
                      <div className="flex items-center justify-between">
                        <h4>Outgoing Call (Human Answered) Actions</h4>
                      </div>

                      <div className="w-full max-w-[400px] h-[400px] flex justify-center items-center">
                        <Pie
                          options={{
                            responsive: true,
                            plugins: {
                              legend: {
                                position: 'right' as const,
                              },
                              title: {
                                display: true,
                                text: 'Outgoing Call Actions Distribution',
                              },
                            },
                          }}
                          data={prepareCallActionsGraphData(
                            outgoingCallActionsData || [],
                          )}
                        />
                      </div>
                    </div>
                  )}

                  {(!incomingCallActionsData ||
                    incomingCallActionsData?.length > 0) && (
                    <div className="flex flex-col gap-4 items-center">
                      <div className="flex items-center justify-between">
                        <h4>Incoming Call Actions</h4>
                      </div>

                      <div className="w-full max-w-[400px] h-[400px] flex justify-center items-center">
                        <Pie
                          options={{
                            responsive: true,
                            plugins: {
                              legend: {
                                position: 'right' as const,
                              },
                              title: {
                                display: true,
                                text: 'Incoming Call Actions Distribution',
                              },
                            },
                          }}
                          data={prepareCallActionsGraphData(
                            incomingCallActionsData || [],
                          )}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </ContentWrapper>
            </div>
          )}
      </Can>
    </div>
  );
};
